  class DynamicForm extends HTMLElement {
 
    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow      = this.attachShadow({ mode: "open" });
      this._users       = [];
      this._fields      = [];
      this._submitLabel = "Submit";
    }
 
    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._render();
    }
 
    // ── C. Property setters ──────────────────────────────────────────────
 
    set users(data) {
      this._users = Array.isArray(data) ? data : [];
      this._render();
    }
 
    set fields(data) {
      this._fields = Array.isArray(data) ? data : [];
      this._render();
    }
 
    set submitLabel(label) {
      this._submitLabel = label || "Submit";
      this._render();
    }
 
    // ── D. Attribute → property bridge ───────────────────────────────────
    static get observedAttributes() { return ["fields", "users", "submit-label"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "fields")       { try { this.fields      = JSON.parse(newVal); } catch {} }
      if (name === "users")        { try { this.users       = JSON.parse(newVal); } catch {} }
      if (name === "submit-label") { this.submitLabel = newVal; }
    }
 
    // ── E. Utilities ─────────────────────────────────────────────────────
 
    _initials(u) {
      return (u.name[0] + u.surname[0]).toUpperCase();
    }
 
    _visibleFields() {
      return this._fields.filter(f => f.visible);
    }
 
    // ── F. SVG icons ──────────────────────────────────────────────────────
 
    _personIcon() {
      return `<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="currentColor" stroke-width="1.3"/>
        <path d="M2 15c0-3.314 2.686-6 6-6s6 2.686 6 6"
              stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
      </svg>`;
    }
 
    _errorIcon() {
      return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/>
        <path d="M6 4v3M6 8.5v.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
      </svg>`;
    }
 
    _sendIcon() {
      return `<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M14 2L7.5 8.5M14 2L9.5 14L7.5 8.5M14 2L2 6.5L7.5 8.5"
              stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
 
    // ── G. Build HTML for a single field ─────────────────────────────────
    _fieldHTML(field) {
      const req  = field.required ? `<span class="req">*</span>` : "";
      const hint = field.hint ? `<p class="hint">${field.hint}</p>` : "";
      let inputHTML = "";
 
      if (field.type === "user-select") {
        const options = this._users.map(u =>
          `<option value="${u.id}">${u.name} ${u.surname} · ${u.alias}</option>`
        ).join("");
        inputHTML = `
          <div class="select-wrap">
            <div class="avatar" id="av-${field.name}">${this._personIcon()}</div>
            <select id="f-${field.name}" class="select-padded" data-type="user-select">
              <option value="">Select a user…</option>
              ${options}
            </select>
          </div>`;
 
      } else if (field.type === "amount") {
        inputHTML = `
          <div class="amount-wrap">
            <span class="currency">CHF</span>
            <input id="f-${field.name}" type="number" min="0" step="0.01"
                   placeholder="0.00" class="amount-input" data-type="amount" />
          </div>`;
 
      } else {
        inputHTML = `
          <input id="f-${field.name}" type="text"
                 placeholder="${field.hint || ''}" data-type="text" />`;
      }
 
      return `
        <div class="field" id="field-${field.name}">
          <label class="label" for="f-${field.name}">${field.label}${req}</label>
          ${hint}
          ${inputHTML}
          <span class="error" id="err-${field.name}">
            ${this._errorIcon()} This field is required.
          </span>
        </div>`;
    }
 
    // ── H. Update the avatar preview in the user-select field ─────────────
    _updateAvatar(fieldName) {
      const sel = this._shadow.getElementById("f-" + fieldName);
      const av  = this._shadow.getElementById("av-" + fieldName);
      if (!sel || !av) return;
 
      const uid = Number(sel.value);
      const u   = this._users.find(x => x.id === uid);
 
      if (!u) {
        av.innerHTML = this._personIcon();
        av.style.background = "";
        av.style.color = "";
        return;
      }
 
      if (u.avatar) {
        const ini = this._initials(u);
        av.innerHTML = `<img src="${u.avatar}" alt="${u.name}"
          onerror="this.parentNode.innerHTML='${ini}'" />`;
      } else {
        av.innerHTML = this._initials(u);
      }
    }
 
    // ── I. Validate a single field, show/hide its error ───────────────────
    _validateField(fieldName) {
      const field = this._fields.find(f => f.name === fieldName);
      if (!field || !field.required) return true;
 
      const el  = this._shadow.getElementById("f-" + fieldName);
      const err = this._shadow.getElementById("err-" + fieldName);
      if (!el || !err) return true;
 
      const val   = el.value.trim();
      const empty = val === "" || val === "0" || val === "0.00";
      err.classList.toggle("visible", empty);
      return !empty;
    }
 
    // ── J. Collect + validate all visible fields, fire event on success ────
    _handleSubmit() {
      const visible  = this._visibleFields();
      const required = visible.filter(f => f.required);
      let valid      = true;
 
      required.forEach(f => { if (!this._validateField(f.name)) valid = false; });
      if (!valid) return;
 
      // Build data payload
      const data = {};
      visible.forEach(f => {
        const el = this._shadow.getElementById("f-" + f.name);
        if (!el) return;
        if (f.type === "user-select") {
          data[f.name] = this._users.find(u => u.id === Number(el.value)) || null;
        } else if (f.type === "amount") {
          data[f.name] = parseFloat(el.value) || 0;
        } else {
          data[f.name] = el.value.trim();
        }
      });
 
      // Dispatch to host page
      this.dispatchEvent(new CustomEvent("form-submit", {
        detail: data, bubbles: true, composed: true
      }));
 
      // Toast feedback
      const u   = data["recipient"] || data[Object.keys(data).find(k => {
        const f = this._fields.find(x => x.name === k);
        return f && f.type === "user-select";
      })];
      const amt = data[Object.keys(data).find(k => {
        const f = this._fields.find(x => x.name === k);
        return f && f.type === "amount";
      })];
 
      let msg = "Submitted successfully.";
      if (u)   msg += ` For ${u.name} ${u.surname}.`;
      if (amt) msg += ` CHF ${parseFloat(amt).toFixed(2)}.`;
 
      const toast     = this._shadow.getElementById("toast");
      const toastText = this._shadow.getElementById("toast-text");
      if (toast && toastText) {
        toastText.textContent = msg;
        toast.classList.add("visible");
      }
 
      // Reset form
      visible.forEach(f => {
        const el  = this._shadow.getElementById("f-" + f.name);
        const err = this._shadow.getElementById("err-" + f.name);
        const av  = this._shadow.getElementById("av-" + f.name);
        if (el)  el.value = "";
        if (err) err.classList.remove("visible");
        if (av)  { av.innerHTML = this._personIcon(); av.style.background = ""; }
      });
    }
 
    // ── K. Bind all interactive events ───────────────────────────────────
    _bindEvents() {
      const root = this._shadow;
 
      // User-select avatar preview
      root.querySelectorAll("select[data-type='user-select']").forEach(el => {
        const fieldName = el.id.replace("f-", "");
        el.addEventListener("change", () => {
          this._updateAvatar(fieldName);
          root.getElementById("toast")?.classList.remove("visible");
        });
      });
 
      // Amount live validation
      root.querySelectorAll("input[data-type='amount']").forEach(el => {
        const fieldName = el.id.replace("f-", "");
        el.addEventListener("input", () => {
          root.getElementById("toast")?.classList.remove("visible");
          this._validateField(fieldName);
        });
      });
 
      // Text inputs — dismiss toast on change
      root.querySelectorAll("input[data-type='text']").forEach(el => {
        el.addEventListener("input", () => {
          root.getElementById("toast")?.classList.remove("visible");
        });
      });
 
      // Submit button
      root.getElementById("submit-btn")?.addEventListener("click", () => {
        this._handleSubmit();
      });
    }
 
    // ── L. Full render ────────────────────────────────────────────────────
    _render() {
      const visible = this._visibleFields();
 
      const bodyParts = visible.map((f, i) =>
        (i > 0 ? `<div class="divider"></div>` : "") + this._fieldHTML(f)
      ).join("");
 
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          :host { display: block; font-family: system-ui, sans-serif; }
 
          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            overflow: hidden;
            /* max-width: 700px; */
          }
 
          .body {
            padding: 1.25rem;
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
 
          .field {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }
 
          .label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #888;
            letter-spacing: 0.02em;
          }
 
          .req {
            color: #c0392b;
            margin-left: 2px;
          }
 
          .hint {
            font-size: 11px;
            color: #aaa;
            margin-top: -2px;
          }
 
          input, select {
            font: inherit;
            font-size: 14px;
            padding: 9px 12px;
            border: 0.5px solid #d0cfc7;
            border-radius: 8px;
            background: #fff;
            color: #1a1a1a;
            outline: none;
            width: 100%;
            -webkit-appearance: none;
          }
 
          input:focus, select:focus { border-color: #888; }
 
          select {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%23888' stroke-width='1.2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            padding-right: 32px;
            cursor: pointer;
          }
 
          .select-wrap {
            position: relative;
            display: flex;
            align-items: center;
          }
 
          .avatar {
            position: absolute;
            left: 10px;
            width: 22px; height: 22px;
            border-radius: 50%;
            background: #A28077;
            color: #F5EDEB;
            display: flex; align-items: center; justify-content: center;
            font-size: 9px;
            font-weight: 500;
            pointer-events: none;
            overflow: hidden;
            flex-shrink: 0;
            z-index: 1;
          }
 
          .avatar img {
            width: 100%; height: 100%;
            object-fit: cover;
            border-radius: 50%;
          }
 
          .select-padded { padding-left: 42px; }
 
          .amount-wrap {
            position: relative;
            display: flex;
            align-items: center;
          }
 
          .currency {
            position: absolute;
            left: 12px;
            font-size: 13px;
            font-weight: 500;
            color: #888;
            pointer-events: none;
            z-index: 1;
          }
 
          .amount-input {
            padding-left: 46px;
            font-variant-numeric: tabular-nums;
          }
 
          /* hide browser spin buttons on number input */
          .amount-input::-webkit-outer-spin-button,
          .amount-input::-webkit-inner-spin-button { -webkit-appearance: none; }
          .amount-input { -moz-appearance: textfield; }
 
          .error {
            display: none;
            align-items: center;
            gap: 4px;
            font-size: 11px;
            color: #c0392b;
            margin-top: -2px;
          }
 
          .error.visible { display: flex; }
 
          .divider {
            height: 0.5px;
            background: #e0dfd7;
            margin: 0 -1.25rem;
          }
 
          .footer {
            padding: 1rem 1.25rem;
            border-top: 0.5px solid #e0dfd7;
          }
 
          .submit-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 11px 20px;
            font: inherit;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            border-radius: 8px;
            /*background: #1a1a1a;*/
            background: #5D4037;
            color: #fff;
            border: none;
            transition: opacity 0.15s, transform 0.1s;
            touch-action: manipulation;
          }
 
          .submit-btn:hover  { opacity: 0.85; }
          .submit-btn:active { transform: scale(0.98); }
          .submit-btn:disabled { opacity: 0.35; cursor: not-allowed; }
 
          .toast {
            display: none;
            align-items: center;
            gap: 8px;
            background: #e6f4ea;
            color: #2d6a4f;
            font-size: 13px;
            padding: 10px 14px;
            border-radius: 8px;
            border: 0.5px solid #b7dfbf;
            margin-top: 12px;
          }
 
          .toast.visible { display: flex; }
 
          .toast-dot {
            width: 7px; height: 7px;
            border-radius: 50%;
            background: #2d6a4f;
            flex-shrink: 0;
          }
 
          @media (max-width: 400px) {
            /*.card  { border-radius: 0; border-left: none; border-right: none; }*/
            .card  { border-left: none; border-right: none; }
            .body  { padding: 1rem; }
            .footer { padding: 1rem; }
            .divider { margin: 0 -1rem; }
            input, select { font-size: 16px; } /* prevent iOS zoom */
          }
 
          /*
          @media (prefers-color-scheme: dark) {
            .card    { background: #1e1e1c; border-color: #333330; }
            .label   { color: #888; }
            .hint    { color: #666; }
            input, select { background: #2a2a28; border-color: #444441; color: #f0ede8; }
            .currency { color: #888; }
            .avatar  { background: #1e3a4f; color: #7ec8f7; }
            .footer  { border-top-color: #333330; }
            .divider { background: #333330; }
          }
          */
        </style>
 
        <div class="card">
          <div class="body">${bodyParts}</div>
          <div class="footer">
            <button class="submit-btn" id="submit-btn">
              ${this._sendIcon()} ${this._submitLabel}
            </button>
            <div class="toast" id="toast">
              <span class="toast-dot"></span>
              <span id="toast-text">Submitted successfully.</span>
            </div>
          </div>
        </div>
      `;
 
      this._bindEvents();
    }
  }

export { DynamicForm }